import EditUserDetail from 'components/EditUserDetail/EditUserDetail';
import { isEmpty } from 'lodash';
import { useMemo } from 'react';

import { UseFormReturn, Controller, useWatch } from 'react-hook-form';
import { IChannelRecording, InheritedRecording } from 'components/ProgramSetupChannelSettings';
import { IProgram } from 'shared/interfaces/IProgram';
import { useProgramTypeOptions } from 'shared/hooks/useProgramTypeOptions';
import { SEGMENT_TYPES } from 'shared/constants/SEGMENT_TYPES';
import { IProgramSetup, IProgramSetupForm, ProgramSetupUser } from 'shared/interfaces/IProgramSetup';
import { Select } from 'shared/theme/elements';
import { AnchorMultiSelect } from './components/AnchorMultiSelect';
import { AutomationToggle } from './components/AutomationToggle';
import {
  Card,
  CardBody,
  CardHead,
  CardIc,
  CardSub,
  CardTitle,
  CardTitles,
  Fld,
  Grid3,
  HintText,
  IdxPill,
  SimGrid,
  SimGroup,
  SimHead,
  ToggleLabel,
  ToggleSub,
} from './ProgramSetup.styled';
import {
  ProgramSetupPageSearchLoading,
  ProgramSetupPageSearchOptions,
  ProgramSetupSearch,
} from './ProgramSetupContainer';

interface Props {
  form: UseFormReturn<IProgramSetupForm, object>;
  isLoading?: boolean;
  options: ProgramSetupPageSearchOptions;
  searchLoading: ProgramSetupPageSearchLoading;
  onSearch: (key: keyof ProgramSetupSearch, value: any) => void;
  programSetup?: IProgramSetup;
  inheritedRecording?: IChannelRecording;
  // The selected program, which owns the Program Type.
  program?: IProgram;
}

export const ProgramSetupDetails = ({
  form,
  isLoading,
  options,
  searchLoading,
  onSearch,
  programSetup,
  inheritedRecording,
  program,
}: Props) => {
  const {
    getValues,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = form;

  const channelObj = useWatch({ control, name: 'channel_obj' });
  const isSimulcast = !!useWatch({ control, name: 'is_simulcast' });
  const stationObj = useWatch({ control, name: 'station_obj' });
  const programType = useWatch({ control, name: 'program_type' });
  const programId = useWatch({ control, name: 'program_obj' });
  const programTypes = useProgramTypeOptions({ value: program?.program_type, label: program?.program_type_label });
  const percentage =
    programTypes.getPercentage(programType) ??
    (programType && programType === program?.program_type ? program?.program_type_percentage : null);
  const channelName = options.tvChannel?.find((option) => option.value === channelObj)?.label as string | undefined;

  const isUpdate = useMemo(() => !!programSetup, [programSetup]);
  const channelDisabled = isLoading || isEmpty(getValues().channel_obj) ? true : false;

  const handleChannelChange = (value: any) => {
    clearErrors('channel_obj');
    setValue('channel_obj', value ? (value as string) : '');
    setValue('program_obj', '');
  };

  const handleProgramChange = (value: any) => {
    clearErrors('program_obj');
    setValue('program_obj', value ? (value as string) : '');
  };

  const handleRadioStationChange = (value: any) => {
    clearErrors('station_program_obj');
    setValue('station_obj', value ? (value as string) : null);
    setValue('station_program_obj', null);
  };

  const handleRadioProgramChange = (value: any) => {
    clearErrors('station_program_obj');
    setValue('station_program_obj', value ? (value as string) : null);
  };

  const handleCountryChange = (value: any) => {
    clearErrors('country');
    setValue('country', value as string);
  };

  const handleSimulcastChange = (on: boolean) => {
    // Turning it off keeps the chosen station/program for this session; they're sent as null on save.
    setValue('is_simulcast', on, { shouldDirty: true });
    if (!on) clearErrors(['station_obj', 'station_program_obj']);
  };

  const handleSegmentTypeChange = (value: any) => {
    clearErrors('segment_type');
    setValue('segment_type', value ? (value as string) : null);
  };

  const getUserName = (user?: ProgramSetupUser) => {
    return user ? `${user.first_name} ${user.last_name}` : '';
  };

  return (
    <Card id="secDetails">
      <CardHead>
        <CardIc>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6M8 13h8M8 17h5" />
          </svg>
        </CardIc>
        <CardTitles>
          <CardTitle>Program Details</CardTitle>
          <CardSub>Basic information about the program</CardSub>
        </CardTitles>
      </CardHead>
      <CardBody>
        {isUpdate && (
          <Grid3 style={{ marginBottom: 20 }}>
            <EditUserDetail
              label="Created by"
              date={new Date(programSetup?.date_created || '')}
              name={getUserName(programSetup?.created_by?.[0])}
            />
            {programSetup && programSetup?.updated_by !== null && (
              <EditUserDetail
                label="Modified by"
                date={new Date(programSetup?.date_updated || '')}
                name={getUserName(programSetup?.updated_by?.[0])}
              />
            )}
          </Grid3>
        )}

        <Grid3>
          <Fld>
            <Select
              label="Channel"
              placeHolder="Select Channel"
              size="middle"
              value={getValues().channel_obj}
              options={options.tvChannel || []}
              onChange={handleChannelChange}
              errorMessage={errors.channel_obj?.message}
              onSearch={(keyword) => onSearch('tvChannel', keyword)}
              filterOption={false}
              showSearch
              autoClearSearchValue
              loading={searchLoading.tvChannel}
              isRequired
              $fullWidth
            />
          </Fld>
          <Fld>
            <Select
              label="Program"
              placeHolder="Select Program"
              size="middle"
              value={isEmpty(getValues('program_obj')) ? undefined : getValues('program_obj')}
              options={options.tvProgram || []}
              onChange={handleProgramChange}
              errorMessage={errors.program_obj?.message}
              disabled={channelDisabled}
              onSearch={(keyword) => onSearch('tvProgram', keyword)}
              showSearch
              filterOption={false}
              autoClearSearchValue
              loading={searchLoading.tvProgram}
              isRequired
              $fullWidth
            />
          </Fld>
          <Fld>
            <Select
              label="Segment Type"
              placeHolder="Select Segment Type"
              size="middle"
              value={isEmpty(getValues().segment_type) ? undefined : getValues().segment_type}
              options={SEGMENT_TYPES}
              onChange={handleSegmentTypeChange}
              errorMessage={errors.segment_type?.message}
              disabled={channelDisabled}
              isRequired
              $fullWidth
            />
          </Fld>

          <Fld>
            <Controller
              control={control}
              name="program_type"
              render={({ field: { value, onChange, onBlur } }) => (
                <Select
                  label="Program Type"
                  placeHolder="Select Program Type"
                  size="middle"
                  value={value || undefined}
                  options={programTypes.options}
                  loading={programTypes.loading}
                  onChange={(selected) => {
                    clearErrors('program_type');
                    onChange(selected || null);
                  }}
                  onBlur={onBlur}
                  errorMessage={errors.program_type?.message}
                  disabled={channelDisabled || isEmpty(programId)}
                  isRequired
                  $fullWidth
                />
              )}
            />
            <IdxPill>
              <span className="k">PERCENTAGE</span>
              <span className="v ps-mono">
                {percentage === null || percentage === undefined ? '—' : `${percentage}%`}
              </span>
            </IdxPill>
          </Fld>
          <Fld>
            <Select
              label="Country"
              placeHolder="Select Country"
              size="middle"
              value={isEmpty(getValues().country) ? undefined : getValues().country}
              options={options.country || []}
              onChange={handleCountryChange}
              errorMessage={errors.country?.message}
              disabled={channelDisabled}
              onSearch={(keyword) => onSearch('country', keyword)}
              showSearch
              filterOption={false}
              autoClearSearchValue
              loading={searchLoading.country}
              isRequired
              $fullWidth
            />
          </Fld>
          <Fld>
            <Controller
              control={control}
              name="recording_title_setup.on_off"
              render={({ field: { value, onChange, onBlur } }) => (
                <Select
                  label="Status"
                  size="middle"
                  value={String(value ?? 1)}
                  items={[
                    { id: '1', value: 'Active' },
                    { id: '0', value: 'Inactive' },
                  ]}
                  onChange={(selected) => onChange(Number(selected))}
                  onBlur={onBlur}
                  disabled={channelDisabled}
                  isRequired
                  $fullWidth
                />
              )}
            />
          </Fld>
          <Fld>
            <Controller
              control={control}
              name="recording_title_setup.sagap_process"
              render={({ field: { value, onChange, onBlur } }) => (
                <Select
                  label="Is From Sagap Process"
                  size="middle"
                  value={String(!!value)}
                  items={[
                    { id: 'true', value: 'Yes' },
                    { id: 'false', value: 'No' },
                  ]}
                  onChange={(selected) => onChange(selected === 'true')}
                  onBlur={onBlur}
                  disabled={channelDisabled}
                  isRequired
                  $fullWidth
                />
              )}
            />
          </Fld>
        </Grid3>

        <SimGroup $off={!isSimulcast}>
          <SimHead>
            <AutomationToggle
              on={isSimulcast}
              onChange={handleSimulcastChange}
              disabled={channelDisabled}
              label="Simulcast"
            />
            <div>
              <ToggleLabel>Simulcast</ToggleLabel>
              <ToggleSub>Enable if this program also airs on a radio station.</ToggleSub>
            </div>
          </SimHead>
          {/* Radio Station unlocks with Simulcast; Radio Program unlocks once a station is chosen. */}
          <SimGrid>
            <Fld>
              <Select
                label="Radio Station"
                placeHolder="Select Radio Station"
                size="middle"
                value={isEmpty(getValues('station_obj')) ? undefined : getValues('station_obj')}
                options={options.radioStation || []}
                onChange={handleRadioStationChange}
                errorMessage={errors.station_obj?.message}
                disabled={channelDisabled || !isSimulcast}
                onSearch={(keyword) => onSearch('radioStation', keyword)}
                showSearch
                filterOption={false}
                autoClearSearchValue
                loading={searchLoading.radioStation}
                allowClear
                isRequired
                $fullWidth
              />
            </Fld>
            <Fld>
              <Select
                label="Radio Program"
                placeHolder="Select Radio Program"
                size="middle"
                value={isEmpty(getValues().station_program_obj) ? undefined : getValues().station_program_obj}
                options={options.radioStationProgram || []}
                onChange={handleRadioProgramChange}
                errorMessage={errors.station_program_obj?.message}
                disabled={channelDisabled || !isSimulcast || isEmpty(stationObj)}
                onSearch={(keyword) => onSearch('radioStationProgram', keyword)}
                showSearch
                filterOption={false}
                autoClearSearchValue
                allowClear
                loading={searchLoading.radioStationProgram}
                $fullWidth
                isRequired={!isEmpty(stationObj)}
              />
              <HintText>
                {!isSimulcast
                  ? 'Turn on Simulcast to pick a radio station and program.'
                  : isEmpty(stationObj)
                  ? 'Select a radio station first.'
                  : ' '}
              </HintText>
            </Fld>
          </SimGrid>
        </SimGroup>

        <InheritedRecording label="Channel" channelName={channelName} recording={inheritedRecording} />
      </CardBody>
    </Card>
  );
};

export const ProgramSetupAnchors = ({ form, isLoading, options, searchLoading, onSearch }: Props) => {
  const {
    getValues,
    setValue,
    clearErrors,
    formState: { errors },
  } = form;
  const channelDisabled = isLoading || isEmpty(getValues().channel_obj) ? true : false;

  return (
    <Card id="secAnchors">
      <CardHead>
        <CardIc $tone="ppl">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </CardIc>
        <CardTitles>
          <CardTitle>Anchor / Reporter</CardTitle>
          <CardSub>Select one or more anchors or reporters</CardSub>
        </CardTitles>
      </CardHead>
      <CardBody>
        <AnchorMultiSelect
          value={getValues().anchors}
          options={options.authors}
          onChange={(anchors) => {
            setValue('anchors', anchors);
            clearErrors('anchors');
          }}
          onSearch={(keyword) => onSearch('authors', keyword)}
          loading={searchLoading.authors}
          disabled={channelDisabled}
        />
        {errors.anchors?.message && (
          <div style={{ marginTop: 7, fontSize: 11.5, color: 'var(--req)', fontWeight: 600 }}>
            {errors.anchors?.message as string}
          </div>
        )}
      </CardBody>
    </Card>
  );
};
